import { CoreEvent, eventNames } from '@carvana/core-events';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { DispatcherEvent, NavPayloadAction } from '@carvana/merch-native';
import type {
  ErrorContext,
  SearchBarContextProps,
  SuggestionSelectionProps,
  TrackEvent
} from '@carvana/merch-search-bar';
import { useCallback, useContext, useMemo } from 'react';
import {
  absoluteBaseUrl,
  apikUrl,
  assetsCdnUrl
} from '~/constants/environment';
import {
  useIsMobile,
  useIsNative,
  useIsTablet
} from '~/context/common/DeviceContext';
import { useSavedSearches } from '~/context/common/SavedSearchesContext';
import { useConfiguration, useConsumer } from '~/context/common/StaticContext';
import UserContext from '~/context/common/UserContext';
import ModalContext from '~/context/home/ModalContext';
import useIsInPriceDropSuppressionExperiment from '~/hooks/common/experiments/useIsInPriceDropSuppressionExperiment';
import sendMessageToNative from '~/native/home/dispatchers/sendMessageToNative';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useSearchBarProps(): SearchBarContextProps {
  const { trackEvent } = useContext(AnalyticsContext);
  const { setSearchBarModalOpen, setIsSearchBarFocused } =
    useContext(ModalContext);
  const savedSearches = useSavedSearches();
  const {
    enableUuidFeatureFiltering,
    priceDropRestrictedStates,
    shouldUseTitleCasing
  } = useConfiguration();
  const consumer = useConsumer();
  const {
    location: {
      data: { state = '' }
    }
  } = useContext(UserContext);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isNative = useIsNative();
  const suppressPriceDropContent = useIsInPriceDropSuppressionExperiment();

  const onCloseModal = useCallback(() => {
    setSearchBarModalOpen(false);
  }, [setSearchBarModalOpen]);

  const onShowModalClick = useCallback(() => {
    setSearchBarModalOpen(true);
  }, [setSearchBarModalOpen]);

  const onSavedSearchViewAll = useCallback(() => {
    trackEvent('Search Bar', {
      action: 'clicked',
      item: 'view all',
      description: 'saved searches'
    });

    if (isNative) {
      sendMessageToNative(DispatcherEvent.Link, {
        action: NavPayloadAction.ViewSavedSearches
      });
    } else {
      CoreEvent.emit(eventNames.favorites.mfe.drawer.open, {
        tab: 'searches'
      });
    }
  }, [isNative, trackEvent]);

  const onSuggestionSelection = useMemo(
    () =>
      isNative
        ? ({ cvnaId, url }: SuggestionSelectionProps) => {
            sendMessageToNative(DispatcherEvent.LeaveFullscreen, {});

            sendMessageToNative(DispatcherEvent.Link, {
              url: `${absoluteBaseUrl}${url}`,
              linkType: 'browser',
              action: NavPayloadAction.ViewSrp,
              cvnaid: cvnaId
            });
          }
        : undefined,
    [isNative]
  );

  const onError = useCallback((error: Error, context: ErrorContext) => {
    generateErrorLogger(`SearchBar - ${context.location}`, context)(error);
  }, []);

  return useMemo(
    () => ({
      apiBaseUrl: apikUrl,
      cdnBaseUrl: assetsCdnUrl,
      classes: { modalButton: 'h-[48px]' },
      disableScrollLock: true,
      enableUuidFeatureFiltering,
      geolocationState: state,
      hideViewAll: true,
      isMobile,
      isTablet,
      onCloseModal,
      onError,
      onFocus: setIsSearchBarFocused,
      onSavedSearchViewAll,
      onShowModalClick,
      onSuggestionSelection,
      priceDropRestrictedStates,
      refSource: consumer,
      savedSearches,
      shouldUseTitleCasing,
      showButton: isMobile,
      size: 'lg',
      suppressPriceDropContent,
      trackEvent: trackEvent as TrackEvent
    }),
    [
      consumer,
      enableUuidFeatureFiltering,
      isMobile,
      isTablet,
      onCloseModal,
      onError,
      onSavedSearchViewAll,
      onShowModalClick,
      onSuggestionSelection,
      priceDropRestrictedStates,
      savedSearches,
      setIsSearchBarFocused,
      shouldUseTitleCasing,
      state,
      suppressPriceDropContent,
      trackEvent
    ]
  );
}
